import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigContext";
import { useHomeUi } from "../../context/HomeUiContext";
import CustomCalendar from "../Date/CustomCalendar";
import ToysGrid from "../Grid/ToysGrid";
import CartDrawer from "../Order/CartDrawer";
import OrderForm from "../Order/OrderForm";
import { useToys } from "../Toy/ToysContext";

const CATALOG_MOBILE_MEDIA = "(max-width: 680px)";

function DatePickerStrip({
  variant,
  headerScrolled,
  containerRef,
  selectedDateLabel,
  headerDateLabel,
  openCal,
  checkAvail,
  calOpen,
  calHost,
  calYear,
  calMonth,
  shiftMonth,
  reservationDate,
  pickDate,
  calendarInline = true,
  anchorRef,
}) {
  const isHeader = variant === "header";
  const showCal = calOpen && calHost === (isHeader ? "header" : "hero");
  const wrapClass = isHeader
    ? `hdr-date-bar${headerScrolled ? " scrolled" : ""}`
    : "hero-date-bar date-bar";

  const fieldId = isHeader ? "dateFieldHeader" : "dateFieldHero";
  const labelShown = isHeader
    ? headerDateLabel ?? selectedDateLabel
    : selectedDateLabel;

  return (
    <div
      className={wrapClass}
      id={isHeader ? undefined : "dateBar"}
      ref={containerRef}
    >
      <div className="date-picker-anchor" ref={anchorRef}>
        <div
          className={isHeader ? "hdr-date-field" : "date-field"}
          id={fieldId}
          role="button"
          tabIndex={0}
          onClick={(e) => openCal(e, isHeader ? "header" : "hero")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ")
              openCal(e, isHeader ? "header" : "hero");
          }}
        >
          <span className={isHeader ? "hdr-date-icon" : "date-icon"}>📅</span>
          <div className={isHeader ? "hdr-date-inner" : "date-inner"}>
            <div className={isHeader ? "hdr-date-label" : "date-label"}>
              Rental Date
            </div>
            <div className={isHeader ? "hdr-date-val" : "date-val"}>
              {labelShown ? (
                <strong>
                  {labelShown.charAt(0).toUpperCase() +
                    labelShown.slice(1)}
                </strong>
              ) : (
                <span className="date-ph">Choose a date</span>
              )}
            </div>
          </div>
        </div>

        {showCal && calendarInline ? (
          <div
            className="cal-wrap open"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <CustomCalendar
              calYear={calYear}
              calMonth={calMonth}
              onShiftMonth={shiftMonth}
              selectedDate={reservationDate}
              onPickDate={pickDate}
            />
          </div>
        ) : null}
      </div>
      <div className={isHeader ? "hdr-date-div" : "date-div"} />
      <button
        type="button"
        className={isHeader ? "hdr-date-cta" : "date-cta"}
        onClick={checkAvail}
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span className="date-search-label">Search</span>
      </button>
    </div>
  );
}

export default function CatalogChrome({
  availBannerShow,
  setAvailBannerShow,
  calOpen,
  setCalOpen,
  calYear,
  calMonth,
  shiftMonth,
  dateToast,
  setDateToast,
  orderFormOpen,
  setOrderFormOpen,
}) {
  const { reservationDate, setReservationDate, cart } = useToys();
  const {
    openDrawer,
    closeDrawer,
    stockLimit,
    closeStockLimit,
    registerRemindSelectRentalDate,
    closeAllToyModals,
  } = useHomeUi();
  const config = useConfig();
  const navigate = useNavigate();
  const noCheckout = config.noCheckout || config.id === "versionNoCheckout";
  const heroDateBarRef = useRef(null);
  const headerDateBarRef = useRef(null);
  const heroDateAnchorRef = useRef(null);
  const headerDateAnchorRef = useRef(null);
  const calPortalRef = useRef(null);
  const [calPopGeo, setCalPopGeo] = useState(null);
  const [headerDateScrolled, setHeaderDateScrolled] = useState(false);
  const [calHost, setCalHost] = useState("hero");

  const selectedDateLabel = reservationDate
    ? reservationDate.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const [catalogMobileLayout, setCatalogMobileLayout] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(CATALOG_MOBILE_MEDIA).matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(CATALOG_MOBILE_MEDIA);
    const onChange = () => setCatalogMobileLayout(mq.matches);
    mq.addEventListener("change", onChange);
    onChange();
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const headerDateLabel =
    reservationDate && catalogMobileLayout
      ? reservationDate.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : selectedDateLabel;

  const availDateLabel = reservationDate ? selectedDateLabel : "—";

  const cartCount = cart.reduce((a, i) => a + i.quantity, 0);
  const totalTtc = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.toy.fields[config.itemPriceFieldTTC],
    0,
  );

  const pickDate = useCallback(
    (d) => {
      setReservationDate(d);
      setCalOpen(false);
      setAvailBannerShow(true);
    },
    [setReservationDate, setAvailBannerShow, setCalOpen],
  );

  const flashSelectDateHint = useCallback(
    ({ openCalendar = false, scrollBehavior = "smooth" } = {}) => {
      setDateToast(true);
      const heroEl = heroDateBarRef.current;
      const useStickyHeaderDate =
        heroEl != null && heroEl.getBoundingClientRect().bottom < 64;
      const scrollTargetId = useStickyHeaderDate
        ? "dateFieldHeader"
        : "dateFieldHero";
      const scrollEl =
        document.getElementById(scrollTargetId) ||
        document.getElementById("dateBar");
      if (scrollEl) {
        const headerPad = useStickyHeaderDate ? 72 : 16;
        const rect = scrollEl.getBoundingClientRect();
        const y = Math.max(
          0,
          rect.top + window.scrollY - headerPad,
        );
        window.scrollTo({ top: y, behavior: scrollBehavior });
      }
      const field = document.getElementById(scrollTargetId);
      if (field) {
        field.style.outline = "2px solid var(--sand)";
        field.style.borderRadius = "100px 0 0 100px";
      }
      if (openCalendar) {
        setCalHost(useStickyHeaderDate ? "header" : "hero");
        setCalOpen(true);
      }
      setTimeout(() => {
        setDateToast(false);
        if (field) {
          field.style.outline = "";
          field.style.borderRadius = "";
        }
      }, 3000);
    },
    [setDateToast, setCalHost, setCalOpen],
  );

  const checkAvail = useCallback(() => {
    if (!reservationDate) {
      flashSelectDateHint({ openCalendar: true });
      return;
    }
    setAvailBannerShow(true);
    setTimeout(() => {
      const el = document.getElementById("availBanner");
      if (el) {
        window.scrollTo({
          top: el.offsetTop - 80,
          behavior: "smooth",
        });
      }
    }, 50);
  }, [reservationDate, setAvailBannerShow, flashSelectDateHint]);

  useEffect(() => {
    const onScroll = () => {
      const heroEl = heroDateBarRef.current;
      if (!heroEl) return;
      setHeaderDateScrolled(heroEl.getBoundingClientRect().bottom < 64);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!calOpen) return;
    const onDoc = (e) => {
      const he = heroDateBarRef.current;
      const hh = headerDateBarRef.current;
      if (he?.contains(e.target) || hh?.contains(e.target)) return;
      if (calPortalRef.current?.contains(e.target)) return;
      setCalOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [calOpen, setCalOpen]);

  const goCheckout = () => {
    if (!reservationDate) {
      flashSelectDateHint();
      return;
    }
    if (!cart.length) {
      window.alert("Please add at least one item.");
      return;
    }
    closeDrawer();
    if (noCheckout) {
      setOrderFormOpen(true);
    } else {
      navigate("/checkout");
    }
  };

  const onMissingRentalDate = useCallback(() => {
    closeDrawer();
    flashSelectDateHint();
  }, [closeDrawer, flashSelectDateHint]);

  useEffect(() => {
    registerRemindSelectRentalDate(() => {
      flashSelectDateHint();
    });
    return () => registerRemindSelectRentalDate(() => {});
  }, [registerRemindSelectRentalDate, flashSelectDateHint]);

  const onDateToastActivate = useCallback(() => {
    closeAllToyModals();
    // After ToyDetail useLayoutEffect cleanup (scroll restore + body unlock);
    // timeout 0 still races on some WebKit builds.
    setTimeout(() => {
      flashSelectDateHint({
        scrollBehavior: "auto",
        openCalendar: true,
      });
    }, 80);
  }, [closeAllToyModals, flashSelectDateHint]);

  const openCal = useCallback(
    (e, host) => {
      e?.stopPropagation();
      const nextHost = host ?? (headerDateScrolled ? "header" : "hero");
      if (calOpen && calHost === nextHost) {
        setCalOpen(false);
        return;
      }
      setCalHost(nextHost);
      setCalOpen(true);
    },
    [headerDateScrolled, calOpen, calHost, setCalOpen],
  );

  /* Hero (non-sticky) calendar on mobile: portaled picker follows scroll and feels broken;
   * lock page scroll like a modal — header/sticky calendar stays scrollable. */
  useLayoutEffect(() => {
    const lock =
      catalogMobileLayout && calOpen && calHost === "hero";
    if (!lock) {
      return undefined;
    }
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.setAttribute("data-scroll-locked", "1");
    return () => {
      document.body.removeAttribute("data-scroll-locked");
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    };
  }, [catalogMobileLayout, calOpen, calHost]);

  useLayoutEffect(() => {
    if (!catalogMobileLayout || !calOpen) {
      setCalPopGeo(null);
      return undefined;
    }

    const measure = () => {
      const el =
        calHost === "header"
          ? headerDateAnchorRef.current
          : heroDateAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 12;
      const minW = 280;
      const vw = window.innerWidth;
      let width = Math.max(r.width, minW);
      width = Math.min(width, vw - 24);
      let left = r.left;
      if (left + width > vw - 12) {
        left = Math.max(12, vw - 12 - width);
      }
      setCalPopGeo({ top: r.bottom + gap, left, width });
    };

    measure();

    if (calHost === "hero") {
      const onResize = () => measure();
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }

    let raf = 0;
    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [catalogMobileLayout, calOpen, calHost]);

  const mobileCalPortal =
    catalogMobileLayout &&
    calOpen &&
    calPopGeo &&
    createPortal(
      <div
        ref={calPortalRef}
        className="cal-wrap open cal-wrap-mobile-portal"
        style={{
          top: calPopGeo.top,
          left: calPopGeo.left,
          width: calPopGeo.width,
        }}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <CustomCalendar
          calYear={calYear}
          calMonth={calMonth}
          onShiftMonth={shiftMonth}
          selectedDate={reservationDate}
          onPickDate={pickDate}
        />
      </div>,
      document.body,
    );

  return (
    <>
      {mobileCalPortal}
      <header className="site-header">
        <div className="hdr">
          <div className="hdr-spacer" aria-hidden />
          <DatePickerStrip
            variant="header"
            headerScrolled={headerDateScrolled}
            containerRef={headerDateBarRef}
            selectedDateLabel={selectedDateLabel}
            headerDateLabel={headerDateLabel}
            openCal={openCal}
            checkAvail={checkAvail}
            calOpen={calOpen}
            calHost={calHost}
            calYear={calYear}
            calMonth={calMonth}
            shiftMonth={shiftMonth}
            reservationDate={reservationDate}
            pickDate={pickDate}
            calendarInline={!catalogMobileLayout}
            anchorRef={headerDateAnchorRef}
          />
          <button
            type="button"
            className="cart-pill"
            onClick={openDrawer}
            aria-label="Open selection"
          >
            🛒 <span className="cart-pill-text">My Selection</span>
            <span className="cart-count">{cartCount}</span>
          </button>
        </div>
      </header>

      <section className="hero">
        <div className="hero-inner">
          <h1>
            Order your
            <br />
            <em>water toys</em>
          </h1>
          <p className="hero-sub">
            Select your rental date, choose your toys, and order instantly.
          </p>

          <DatePickerStrip
            variant="hero"
            headerScrolled={false}
            containerRef={heroDateBarRef}
            selectedDateLabel={selectedDateLabel}
            openCal={openCal}
            checkAvail={checkAvail}
            calOpen={calOpen}
            calHost={calHost}
            calYear={calYear}
            calMonth={calMonth}
            shiftMonth={shiftMonth}
            reservationDate={reservationDate}
            pickDate={pickDate}
            calendarInline={!catalogMobileLayout}
            anchorRef={heroDateAnchorRef}
          />
        </div>
      </section>

      <div className="info-strip mb-8">
        <div className="info-strip-inner">
          <div className="info-item">
            <span>📍</span>
            <span>Delivery between Monaco &amp; Saint-Tropez</span>
          </div>
          <div className="info-sep" />
          <div className="info-item">
            <span>⚡</span>
            <span>Last-minute orders accepted</span>
          </div>
          <div className="info-sep" />
          <div className="info-item">
            <span>📅</span>
            <span>Open 7 days a week</span>
          </div>
        </div>
      </div>

      <div
        className={`avail ${availBannerShow ? "show" : ""}`}
        id="availBanner"
      >
        <div className="avail-inner">
          <div className="avail-text">
            <strong>Availability confirmed</strong> — showing all toys available
            on <span id="availDate">{availDateLabel}</span>.
          </div>
        </div>
      </div>

      <main className="catalog-main">
        <ToysGrid />
      </main>

      <div className={`sticky-bar ${cartCount > 0 ? "show" : ""}`}>
        <div className="sticky-spacer" />
        <div className="sticky-left">
          <div className="sticky-items">
            {cartCount} item{cartCount !== 1 ? "s" : ""} selected
          </div>
          <div className="sticky-total">€{totalTtc.toLocaleString()}</div>
        </div>
        <button type="button" className="sticky-cta" onClick={openDrawer}>
          Check out
          <svg viewBox="0 0 24 24" aria-hidden>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>

      <CartDrawer
        onRequestQuote={() => setOrderFormOpen(true)}
        onMissingRentalDate={onMissingRentalDate}
        onGoCheckout={goCheckout}
      />

      <div
        className={`date-toast ${dateToast ? "show" : ""}`}
        id="dateToast"
        role="button"
        tabIndex={dateToast ? 0 : -1}
        aria-label="Close and choose rental date"
        onClick={dateToast ? onDateToastActivate : undefined}
        onKeyDown={
          dateToast
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onDateToastActivate();
                }
              }
            : undefined
        }
      >
        📅 Please select a rental date first
      </div>

      {stockLimit ? (
        <div
          className="stock-popup-bg open"
          onClick={closeStockLimit}
          role="presentation"
        >
          <div
            className="stock-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="stock-popup-title"
          >
            <div className="stock-popup-top">
              <span className="stock-popup-icon">📦</span>
              <div className="stock-popup-title" id="stock-popup-title">
                Maximum quantity reached
              </div>
              <div className="stock-popup-msg">
                Only <strong>{stockLimit.max}</strong> unit
                {stockLimit.max !== 1 ? "s" : ""} of{" "}
                <strong>{stockLimit.name}</strong>{" "}
                {stockLimit.max === 1 ? "is" : "are"} available for your rental
                date.
              </div>
            </div>
            <div className="stock-popup-foot">
              <button
                type="button"
                className="stock-popup-btn"
                onClick={closeStockLimit}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <OrderForm isOpen={orderFormOpen} setIsOpen={setOrderFormOpen} />
    </>
  );
}
